'use client';

// import { fullPageLoadingIndicator } from '@/components/LoadingIndicator';
import ProductFullDetail from '@/components/ProductFullDetail';
import RelatedProducts from '@/components/RelatedProducts';
import ProductReviewsLazyLoad from './product-reviews-lazy-load';
// import Reviews from '@/components/Reviews';
// import { useGetProduct } from '@/hooks/Product/use-get-product';
import { pushEcommerceEvent } from '@/lib/data-layer';
import { PageViewEvent } from '@/lib/use-page-view-event';
import { getNewUrlKey, getProductFullDetailSeo } from '@/lib/get-product-full-detail-seo';
import { useSearchParams } from 'next/navigation';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import FeatureIcon from '../ProductFullDetail/feature-icon';
import { getSortedColor } from '../ProductFullDetail/get-sorted-color';
import { Features } from './features';

const compareStringsIgnoreCaseAndSpace = (str1: string, str2: string): boolean => {
  if (!str1 || !str2) {
    return false;
  }

  // 去除前后空格，并用中横线替换内部空格
  const replacedStr1 = str1.trim().toLowerCase().replace(/\s+/g, '-');
  const replacedStr2 = str2.trim().toLowerCase().replace(/\s+/g, '-');

  return replacedStr1 === replacedStr2;
};

const REVIEW_LIST_ANCHOR_ID = 'product-review-list';

export const ProductPage = ({
  urlKey, // 和地址栏一样
  originalUrlKey, // 不带颜色的产品 url
  seoTitle,
  seoDescription,
  defaultColorLabel,
  seoKeywords,
  product,
  features,
  breadcrumbsData,
}: {
  urlKey: string;
  originalUrlKey: string;
  seoTitle: string;
  seoDescription: string;
  defaultColorLabel: string;
  seoKeywords: string;
  product: any;
  features: any;
  breadcrumbsData: any;
}) => {
  const [seoColorLabel, setSeoColorLabel] = useState(defaultColorLabel);

  const reviewHeaderRef = useRef<HTMLDivElement | null>(null);
  const trackedViewItemRef = useRef<string | null>(null);

  const searchParams = useSearchParams();

  useEffect(() => {
    if (!product || !product.sku || trackedViewItemRef.current === product.sku) {
      return;
    }

    const productPrice = product?.price_range?.maximum_price;
    const finalPrice = productPrice?.final_price;

    if (!finalPrice?.currency || typeof finalPrice?.value !== 'number') {
      return;
    }

    let categoryParams: Record<string, string> = {};
    if (breadcrumbsData?.categories?.items?.length) {
      const firstItem = breadcrumbsData.categories.items[0];
      const crumbs = firstItem.breadcrumbs
        ? [...firstItem.breadcrumbs].sort((a: any, b: any) => a.category_level - b.category_level).map((c: any) => c.category_name)
        : [];
      if (firstItem.name) {
        crumbs.push(firstItem.name);
      }
      crumbs.forEach((name, index) => {
        if (index === 0) {
          categoryParams['item_category'] = name;
        } else if (index < 5) {
          categoryParams[`item_category${index + 1}`] = name;
        }
      });
    }

    pushEcommerceEvent({
      event: 'view_item',
      ecommerce: {
        currency: finalPrice.currency,
        value: finalPrice.value,
        items: [
          {
            item_name: product.name,
            item_id: product.sku,
            price: finalPrice.value,
            quantity: 1,
            ...categoryParams,
          },
        ],
      },
    });

    trackedViewItemRef.current = product.sku;
  }, [product, breadcrumbsData]);

  const defaultValues = useMemo(() => {
    if (product.__typename === 'SimpleProduct') return { quantity: '1' };

    return product?.configurable_options?.reduce(
      (acc: Record<string, any>, opts: any) => {
        const optionType = opts?.label?.toLowerCase();

        const isColor = optionType.startsWith('lw_color') || optionType.startsWith('color');

        if (isColor) {
          const sortedColor = getSortedColor({
            values: opts?.values,
            variants: product.variants,
            attribute_code: opts?.attribute_code,
          });
          const firstColor = sortedColor[0];

          const urlColor = searchParams.get('color');
          const defaultValues_url = sortedColor?.find(item =>
            compareStringsIgnoreCaseAndSpace(item?.label || '', defaultColorLabel || ''),
          );
          const defaultValues_searchParams = sortedColor?.find(item =>
            compareStringsIgnoreCaseAndSpace(item?.label || '', urlColor || ''),
          );
          const defaultValues = defaultValues_url || defaultValues_searchParams;

          acc[opts?.attribute_code] = defaultValues
            ? {
                uid: defaultValues?.uid,
                label: defaultValues?.label,
                option_attribute_id: opts?.attribute_id,
                option_selection_value: opts.values.find((v: any) => v.uid.toString() === defaultValues?.uid)
                  ?.value_index,
              }
            : {
                uid: firstColor?.uid,
                label: firstColor?.label,
                option_attribute_id: opts?.attribute_id,
                option_selection_value: firstColor?.value_index,
              };
        }

        if (optionType && !isColor) {
          // 从 URL 中获取对应的搜索参数
          const searchValue = searchParams.get(optionType.split(' ').join('-'));

          if (searchValue) {
            const defaultValues = opts?.values?.find((item: any) =>
              compareStringsIgnoreCaseAndSpace(item?.label || '', searchValue || ''),
            );

            if (defaultValues) {
              acc[opts?.attribute_code] = defaultValues
                ? {
                    uid: defaultValues?.uid,
                    label: defaultValues?.label,
                    option_attribute_id: opts?.attribute_id,
                    option_selection_value: opts.values.find((v: any) => v.uid.toString() === defaultValues?.uid)
                      ?.value_index,
                  }
                : undefined;
            }
          }
        }

        return acc;
      },
      { quantity: '1' },
    );
  }, [defaultColorLabel, product.__typename, product?.configurable_options, product?.variants, searchParams]);

  const onAddReviewClick = () => {
    reviewHeaderRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const onViewReviews = () => {
    document.getElementById(REVIEW_LIST_ANCHOR_ID)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const handleRefreshUrlKey = (colorLabel: string) => {
    const newUrlKey = getNewUrlKey({
      colorLabel,
      originalUrlKey: product.url_key,
      searchParamsString: searchParams.toString(),
    });

    window.history.replaceState(null, '', newUrlKey);
  };

  const handleRefreshSeoInfo = ({
    description,
    keywords,
    title,
    colorLabel,
  }: {
    title: string;
    keywords: string;
    description: string;
    colorLabel: string;
  }) => {
    document.title = title;

    const metaElement = document.querySelector('meta[name="description"]');

    if (metaElement) {
      metaElement.setAttribute('content', description);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');

    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    }

    const newUrlKey = getNewUrlKey({
      colorLabel,
      originalUrlKey: product.url_key,
      searchParamsString: searchParams.toString(),
    });

    const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;

    if (canonicalLink) {
      canonicalLink.href = 'https://www.hisea.com/' + newUrlKey.replace(/^\//, '');
    }
  };

  const handleColorLabelChange = (colorLabel: string) => {
    if (colorLabel === seoColorLabel) {
      return;
    }

    setSeoColorLabel(colorLabel);
    handleRefreshUrlKey(colorLabel);
    const { description, keywords, title } = getProductFullDetailSeo({
      meta_description: seoDescription,
      meta_keyword: seoKeywords,
      meta_title: seoTitle,
      defaultColorLabel: colorLabel,
    });

    handleRefreshSeoInfo({ description, keywords, title, colorLabel });
  };

  return (
    <Fragment>
      <PageViewEvent pageTitle={seoTitle} />
      <ProductFullDetail
        onAddReviewClick={onAddReviewClick}
        onViewReviews={onViewReviews}
        product={product}
        handleColorLabelChange={handleColorLabelChange}
        defaultValues={defaultValues}
        seoColorLabel={seoColorLabel}
        breadcrumbsData={breadcrumbsData}
      />
      <Features features={features} />
      <FeatureIcon featureIcon={product.feature_icon} />
      <RelatedProducts urlKey={originalUrlKey} />
      <div ref={reviewHeaderRef}></div>
      <ProductReviewsLazyLoad
        sku={product?.sku ?? ''}
        reviewListAnChorId={REVIEW_LIST_ANCHOR_ID}
        originalUrlKey={originalUrlKey}
        productId={product.id}
      />
    </Fragment>
  );
};
